import { useState } from 'react';
import { Plus, Trash2, GripVertical, Star, Check, X } from 'lucide-react';
import {
  useLeadPipelines,
  useCreatePipeline,
  useUpdatePipeline,
  useDeletePipeline,
  useCreateStage,
  useUpdateStage,
  useDeleteStage,
  useReorderStages,
} from '../../hooks/useLeadPipelines';
import {
  LEAD_PIPELINE_KINDS,
  LEAD_STAGE_TYPES,
  type LeadPipeline,
  type LeadPipelineKind,
  type LeadStage,
  type LeadStageType,
} from '../../lib/leadsApi';
import { Select } from '../ui/Select';
import { useConfirm } from '../ui/ConfirmProvider';

// Settings → "Leads Pipelines" (CLE-193, spec 3.4).
//
// Pipeline configuration UI:
//   - List pipelines, create new (name + description + kind), set default.
//   - Kind: a lead won in a Vânzări (SALES) pipeline becomes a client and moves
//     into the Onboarding pipeline (backend services/leadWon.ts).
//   - Per pipeline: drag-to-reorder stages, add/edit/delete stages with name,
//     color picker, and type (Active/Won/Lost/Archived).
//   - Client-side invariant enforced (mirrors the backend): a pipeline must keep
//     ≥1 ACTIVE and ≥1 WON stage. Stage deletes are blocked when leads exist
//     (backend returns STAGE_HAS_LEADS with a count, surfaced here).
//   - All mutations invalidate the shared ['lead-pipelines'] query so the Leads
//     board reflects changes immediately.

const PIPELINE_KIND_LABELS: Record<LeadPipelineKind, string> = {
  SALES: 'Vânzări',
  ONBOARDING: 'Onboarding',
};

const PIPELINE_KIND_OPTIONS = LEAD_PIPELINE_KINDS.map((k) => ({
  value: k,
  label: PIPELINE_KIND_LABELS[k],
}));

const STAGE_TYPE_LABELS: Record<LeadStageType, string> = {
  ACTIVE: 'Activ',
  WON: 'Câștigat',
  LOST: 'Pierdut',
  ARCHIVED: 'Arhivat',
};

// Quick palette offered alongside the native color input.
const COLOR_SWATCHES = [
  '#3b82f6',
  '#eab308',
  '#22c55e',
  '#16a34a',
  '#a855f7',
  '#f97316',
  '#ef4444',
  '#64748b',
];

const DEFAULT_STAGE_COLOR = '#3b82f6';

function countByType(stages: LeadStage[], type: LeadStageType): number {
  return stages.filter((s) => s.type === type).length;
}

/** Mirror of the backend invariant: ≥1 ACTIVE and ≥1 WON stage. */
function invariantViolation(stages: LeadStage[]): string | null {
  if (countByType(stages, 'ACTIVE') < 1)
    return 'Un pipeline trebuie să aibă cel puțin o etapă Activă.';
  if (countByType(stages, 'WON') < 1)
    return 'Un pipeline trebuie să aibă cel puțin o etapă Câștigat.';
  return null;
}

export function LeadsPipelinesSettings() {
  const { data: pipelines, isLoading, isError, error } = useLeadPipelines();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Default to the first (default) pipeline once data loads.
  const selected = pipelines?.find((p) => p.id === selectedId) ?? pipelines?.[0] ?? null;

  return (
    <div className="max-w-4xl">
      <p className="text-sm text-gray-500">
        Configurați pipeline-urile și etapele lor. Etapele determină coloanele de pe pagina Pipeline
        — modificările de aici se reflectă acolo imediat. Fiecare pipeline păstrează cel puțin o
        etapă Activă și o etapă Câștigat.
      </p>
      <p className="mt-2 text-sm text-gray-500">
        Când un lead ajunge pe etapa Câștigat a unui pipeline de Vânzări, devine automat client și
        trece în pipeline-ul de Onboarding.
      </p>
      {pipelines && !pipelines.some((p) => p.kind === 'ONBOARDING') && (
        <p className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700">
          Nu există niciun pipeline de Onboarding: la câștigare se creează doar clientul, iar
          lead-ul rămâne în Vânzări.
        </p>
      )}

      {isLoading && <p className="mt-6 text-sm text-gray-500">Se încarcă pipeline-urile...</p>}
      {isError && (
        <p className="mt-6 text-sm text-red-600">
          Pipeline-urile nu au putut fi încărcate
          {error instanceof Error ? `: ${error.message}` : ''}.
        </p>
      )}

      {pipelines && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[260px_1fr]">
          <PipelineList
            pipelines={pipelines}
            selectedId={selected?.id ?? null}
            onSelect={setSelectedId}
          />
          {selected ? (
            <PipelineEditor key={selected.id} pipeline={selected} />
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
              Niciun pipeline selectat. Creați unul pentru a începe.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Pipeline list + create -------------------------------------------------

function PipelineList({
  pipelines,
  selectedId,
  onSelect,
}: {
  pipelines: LeadPipeline[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const createPipeline = useCreatePipeline();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<LeadPipelineKind>('SALES');
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);
    try {
      const created = await createPipeline.mutateAsync({
        name: trimmed,
        description: description.trim() || null,
        kind,
      });
      setName('');
      setDescription('');
      setKind('SALES');
      setCreating(false);
      onSelect(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Pipeline-ul nu a putut fi creat.');
    }
  }

  return (
    <div>
      <div className="flex flex-col gap-1">
        {pipelines.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            className={`flex items-center justify-between rounded-md px-3 py-2 text-left text-sm ${
              p.id === selectedId
                ? 'bg-brand-50 font-medium text-brand-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span className="truncate">{p.name}</span>
            {p.kind === 'ONBOARDING' && (
              <span className="ml-2 rounded-full bg-brand-500/10 px-2 py-0.5 text-xs font-medium text-brand-600">
                Onboarding
              </span>
            )}
            {p.isDefault && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                <Star size={11} className="fill-amber-500 text-amber-500" /> Implicit
              </span>
            )}
          </button>
        ))}
      </div>

      {creating ? (
        <div className="mt-3 rounded-md border border-gray-200 p-3">
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nume pipeline"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descriere (opțional)"
            rows={2}
            className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <Select
            label="Tip"
            value={kind}
            onChange={(v) => setKind((v || 'SALES') as LeadPipelineKind)}
            options={PIPELINE_KIND_OPTIONS}
            className="mt-2"
          />
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={handleCreate}
              disabled={!name.trim() || createPipeline.isPending}
              className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {createPipeline.isPending ? 'Se creează...' : 'Creează'}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setError(null);
                setName('');
                setDescription('');
                setKind('SALES');
              }}
              className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Anulează
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            Pipeline-urile noi sunt populate cu etape implicite (Nou, Contactat, Calificat,
            Contractat, Pierdut).
          </p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="mt-3 inline-flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
        >
          <Plus size={15} /> Pipeline nou
        </button>
      )}
    </div>
  );
}

// --- Pipeline editor (metadata + stages) ------------------------------------

function PipelineEditor({ pipeline }: { pipeline: LeadPipeline }) {
  const updatePipeline = useUpdatePipeline();
  const deletePipeline = useDeletePipeline();
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState(pipeline.name);
  const [description, setDescription] = useState(pipeline.description ?? '');
  const [kind, setKind] = useState<LeadPipelineKind>(pipeline.kind);

  const nameDirty = name.trim() !== pipeline.name;
  const descDirty = description.trim() !== (pipeline.description ?? '');
  const kindDirty = kind !== pipeline.kind;
  const metaDirty = (nameDirty || descDirty || kindDirty) && name.trim().length > 0;

  async function handleSaveMeta() {
    setError(null);
    try {
      await updatePipeline.mutateAsync({
        id: pipeline.id,
        input: { name: name.trim(), description: description.trim() || null, kind },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Pipeline-ul nu a putut fi salvat.');
    }
  }

  async function handleSetDefault() {
    if (pipeline.isDefault) return;
    setError(null);
    try {
      await updatePipeline.mutateAsync({ id: pipeline.id, input: { isDefault: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Pipeline-ul implicit nu a putut fi setat.');
    }
  }

  async function handleDelete() {
    setError(null);
    if (
      !(await confirm({
        message: `Ștergeți pipeline-ul "${pipeline.name}"? Această acțiune nu poate fi anulată.`,
        danger: true,
      }))
    )
      return;
    try {
      await deletePipeline.mutateAsync(pipeline.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Pipeline-ul nu a putut fi șters.');
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex flex-col gap-3 border-b border-gray-100 pb-4">
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Nume pipeline
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Descriere
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Descriere opțională"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <div>
          <Select
            label="Tip"
            value={kind}
            onChange={(v) => setKind((v || 'SALES') as LeadPipelineKind)}
            options={PIPELINE_KIND_OPTIONS}
            className="w-full sm:w-56"
          />
          <p className="mt-1 text-xs text-gray-500">
            {kind === 'SALES'
              ? 'La câștigare, lead-ul devine client și trece în pipeline-ul de Onboarding.'
              : 'Aici ajung automat lead-urile câștigate; la ultima etapă nu se întâmplă nimic special.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleSaveMeta}
            disabled={!metaDirty || updatePipeline.isPending}
            className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Salvează modificările
          </button>
          <button
            type="button"
            onClick={handleSetDefault}
            disabled={pipeline.isDefault || updatePipeline.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Star size={14} className={pipeline.isDefault ? 'fill-amber-500 text-amber-500' : ''} />
            {pipeline.isDefault ? 'Pipeline implicit' : 'Setează ca implicit'}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deletePipeline.isPending}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Trash2 size={14} /> Șterge pipeline
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
          {error}
        </p>
      )}

      <StageManager pipeline={pipeline} />
    </div>
  );
}

// --- Stage manager (list, reorder, add, edit, delete) -----------------------

function StageManager({ pipeline }: { pipeline: LeadPipeline }) {
  const reorderStages = useReorderStages();
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stages = [...pipeline.stages].sort((a, b) => a.order - b.order);
  const invariant = invariantViolation(stages);

  function handleDrop(targetId: string) {
    const sourceId = dragId;
    setDragId(null);
    setOverId(null);
    if (!sourceId || sourceId === targetId) return;

    const ids = stages.map((s) => s.id);
    const from = ids.indexOf(sourceId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(from, 1);
    ids.splice(to, 0, sourceId);

    setError(null);
    reorderStages.mutate(
      { pipelineId: pipeline.id, stageIds: ids },
      {
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Etapele nu au putut fi reordonate.'),
      },
    );
  }

  return (
    <div className="mt-5">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Etape</h3>
        <span className="text-xs text-gray-400">Trageți pentru a reordona</span>
      </div>

      {invariant && (
        <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-700">
          {invariant}
        </p>
      )}
      {error && (
        <p className="mt-2 rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-red-600">
          {error}
        </p>
      )}

      <ul className="mt-3 flex flex-col gap-2">
        {stages.map((stage) => (
          <li
            key={stage.id}
            draggable
            onDragStart={(e) => {
              setDragId(stage.id);
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDragEnd={() => {
              setDragId(null);
              setOverId(null);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setOverId(stage.id);
            }}
            onDrop={() => handleDrop(stage.id)}
            className={`rounded-md border ${
              overId === stage.id && dragId && dragId !== stage.id
                ? 'border-brand-400 ring-1 ring-brand-300'
                : 'border-gray-200'
            } ${dragId === stage.id ? 'opacity-50' : ''}`}
          >
            <StageRow stage={stage} stages={stages} />
          </li>
        ))}
      </ul>

      <AddStageRow pipeline={pipeline} />
    </div>
  );
}

function StageRow({ stage, stages }: { stage: LeadStage; stages: LeadStage[] }) {
  const updateStage = useUpdateStage();
  const deleteStage = useDeleteStage();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(stage.name);
  const [color, setColor] = useState(stage.color);
  const [type, setType] = useState<LeadStageType>(stage.type);
  const [error, setError] = useState<string | null>(null);

  // Would removing/altering this stage break the ≥1 ACTIVE / ≥1 WON invariant?
  function projectedTypeViolation(nextType: LeadStageType): string | null {
    const projected = stages.map((s) => (s.id === stage.id ? { ...s, type: nextType } : s));
    return invariantViolation(projected as LeadStage[]);
  }

  function deleteWouldViolate(): string | null {
    const remaining = stages.filter((s) => s.id !== stage.id);
    return invariantViolation(remaining);
  }

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Numele este obligatoriu.');
      return;
    }
    const violation = projectedTypeViolation(type);
    if (violation) {
      setError(violation);
      return;
    }
    setError(null);
    try {
      await updateStage.mutateAsync({
        stageId: stage.id,
        input: { name: trimmed, color, type },
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Etapa nu a putut fi salvată.');
    }
  }

  async function handleDelete() {
    const violation = deleteWouldViolate();
    if (violation) {
      setError(violation);
      return;
    }
    setError(null);
    try {
      await deleteStage.mutateAsync(stage.id);
    } catch (err) {
      // Backend blocks deletes when leads reference the stage (STAGE_HAS_LEADS)
      // and reports the count in the message.
      setError(err instanceof Error ? err.message : 'Etapa nu a putut fi ștearsă.');
    }
  }

  if (editing) {
    return (
      <div className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-8 w-9 cursor-pointer rounded border border-gray-300"
            aria-label="Culoare etapă"
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="min-w-[140px] flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            placeholder="Nume etapă"
          />
          <Select
            label="Tip etapă"
            hideLabel
            size="sm"
            value={type}
            onChange={(v) => setType(v as LeadStageType)}
            options={LEAD_STAGE_TYPES.map((t) => ({ value: t, label: STAGE_TYPE_LABELS[t] }))}
            className="w-32"
          />
          <button
            type="button"
            onClick={handleSave}
            disabled={updateStage.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60"
          >
            <Check size={14} /> Salvează
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setError(null);
              setName(stage.name);
              setColor(stage.color);
              setType(stage.type);
            }}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <X size={14} /> Anulează
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {COLOR_SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              style={{ backgroundColor: c }}
              className={`h-5 w-5 rounded-full ${
                color.toLowerCase() === c.toLowerCase() ? 'ring-2 ring-offset-1 ring-gray-400' : ''
              }`}
              aria-label={`Folosește culoarea ${c}`}
            />
          ))}
        </div>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-2.5">
      <GripVertical size={16} className="cursor-grab text-gray-300" />
      <span
        className="h-3.5 w-3.5 shrink-0 rounded-full"
        style={{ backgroundColor: stage.color }}
      />
      <span className="flex-1 truncate text-sm font-medium text-gray-800">{stage.name}</span>
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
        {STAGE_TYPE_LABELS[stage.type]}
      </span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
      >
        Editează
      </button>
      <button
        type="button"
        onClick={handleDelete}
        disabled={deleteStage.isPending}
        className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-60"
        aria-label="Șterge etapă"
      >
        <Trash2 size={15} />
      </button>
      {error && (
        <span className="basis-full pt-1 text-sm text-red-600" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

function AddStageRow({ pipeline }: { pipeline: LeadPipeline }) {
  const createStage = useCreateStage();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_STAGE_COLOR);
  const [type, setType] = useState<LeadStageType>('ACTIVE');
  const [error, setError] = useState<string | null>(null);

  async function handleAdd() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);
    try {
      await createStage.mutateAsync({
        pipelineId: pipeline.id,
        input: { name: trimmed, color, type },
      });
      setName('');
      setColor(DEFAULT_STAGE_COLOR);
      setType('ACTIVE');
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Etapa nu a putut fi adăugată.');
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
      >
        <Plus size={15} /> Adaugă etapă
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-gray-200 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          className="h-8 w-9 cursor-pointer rounded border border-gray-300"
          aria-label="Stage color"
        />
        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Stage name"
          className="min-w-[140px] flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        />
        <Select
          label="Tip etapă"
          hideLabel
          size="sm"
          value={type}
          onChange={(v) => setType(v as LeadStageType)}
          options={LEAD_STAGE_TYPES.map((t) => ({ value: t, label: STAGE_TYPE_LABELS[t] }))}
          className="w-32"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!name.trim() || createStage.isPending}
          className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {createStage.isPending ? 'Se adaugă...' : 'Adaugă'}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setError(null);
            setName('');
          }}
          className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Anulează
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {COLOR_SWATCHES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setColor(c)}
            style={{ backgroundColor: c }}
            className={`h-5 w-5 rounded-full ${
              color.toLowerCase() === c.toLowerCase() ? 'ring-2 ring-offset-1 ring-gray-400' : ''
            }`}
            aria-label={`Use color ${c}`}
          />
        ))}
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
